// Procedural pairing-screen background ported from the website hero
// (HeroVeil.astro in bohemica-signage-website-2024): a drifting blue particle
// field cover-fit from a 1440×839 art space, the left half quantised to a 30px
// mosaic, under a grid overlay fading toward the right and bottom. Runs only
// while the canvas is on-screen. Dark palette mirrors the website exactly;
// light is a pastel variant; 'auto' follows prefers-color-scheme live. The
// resolved scheme also toggles `.pair-dark` on the pairing and finishing
// containers so the CSS overrides flip with it.
const W = 1440;
const H = 839;
const CELL = 30; // mosaic / grid cell size in CSS px
const PIX_EDGE = 845; // art-space x where the mosaic fades into the smooth cloud
const PIX_SOFT = 170;
const FADE_START = 0.12; // fraction of height where the wave fade begins
const FADE_END = 0.68; // wave fully faded to base by here
const GRID_FADE = 0.22; // grid stays this far down, then fades to the bottom
const DARK_PALETTE = {
    base: '#080d16',
    baseRgb: '8,13,22',
    blue: [24, 88, 163],
    fieldAlpha: 1,
    gridLine: 'rgba(130,160,205,0.14)',
    clearAlpha: 0.4,
};
const LIGHT_PALETTE = {
    base: '#ffffff',
    baseRgb: '255,255,255',
    blue: [96, 170, 240], // pastel so puff edges stay soft on white
    fieldAlpha: 0.48,
    gridLine: 'rgba(60,105,170,0.14)',
    clearAlpha: 0.55,
};
// Keep-clear pool: fades the wave back to the base behind the pairing content.
// 'left' matches the kiosk's fixed left:58px/top:216px block (CSS px); 'center'
// tracks the web card in the middle of the viewport.
const CLEAR_LEFT_X = 360;
const CLEAR_LEFT_Y = 400;
const CLEAR_R0 = 240;
const CLEAR_R1 = 640;
const CLEAR_CENTER_R0 = 340;
const CLEAR_CENTER_R1 = 900;
let dark = false;
let clearAnchor = 'left';
let schemeReady = false;
// live canvas → disposer (stops its loop, disconnects its observers)
const liveCanvases = new Map();
function pal() {
    return dark ? DARK_PALETTE : LIGHT_PALETTE;
}
function applyDarkClasses() {
    for (const id of ['device-id-container', 'device-finishing-container']) {
        document.getElementById(id)?.classList.toggle('pair-dark', dark);
    }
}
function makeSized(width, height) {
    const c = document.createElement('canvas');
    c.width = width;
    c.height = height;
    return c;
}
// One soft round sprite (white → transparent), reused for every puff.
function buildSprite() {
    const S = 256;
    const sp = makeSized(S, S);
    const c = sp.getContext('2d');
    const g = c.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.4, 'rgba(255,255,255,0.5)');
    g.addColorStop(0.75, 'rgba(255,255,255,0.12)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, S, S);
    return sp;
}
let sprite;
// Emitters trace the wave footprint (left lens → diagonal beam → right cloud).
const EMITTERS = [
    { cx: 1150, cy: 130, rx: 300, ry: 140, a: 0.95, n: 16 },
    { cx: 1330, cy: 175, rx: 210, ry: 150, a: 0.8, n: 11 },
    { cx: 985, cy: 120, rx: 200, ry: 95, a: 0.72, n: 9 },
    { cx: 845, cy: 158, rx: 160, ry: 82, a: 0.5, n: 7 },
    { cx: 640, cy: 198, rx: 250, ry: 60, a: 0.62, n: 10 },
    { cx: 450, cy: 255, rx: 230, ry: 70, a: 0.8, n: 11 },
    { cx: 215, cy: 312, rx: 220, ry: 95, a: 1.0, n: 12 },
    { cx: 175, cy: 320, rx: 120, ry: 60, a: 0.95, n: 6 },
];
const rand = Math.random;
const gauss = () => (rand() + rand() + rand() - 1.5) / 1.5; // ~bell in [-1,1]
function respawn(p, stagger) {
    const em = EMITTERS[p.e];
    p.x0 = em.cx + gauss() * em.rx;
    p.y0 = em.cy + gauss() * em.ry;
    p.life = 6 + rand() * 7; // seconds
    p.age = stagger ? rand() * p.life : 0;
    p.r0 = 46 + rand() * 64;
    p.rg = 0.7 + rand() * 1.0;
    const ang = -Math.PI / 2 + gauss() * 1.7; // up-ish, wide spread
    const spd = 2 + rand() * 6;
    p.vx = Math.cos(ang) * spd;
    p.vy = Math.sin(ang) * spd;
    p.base = em.a * (0.2 + rand() * 0.32);
    p.swx = 14 + rand() * 40;
    p.swy = 10 + rand() * 28;
    p.fwx = 0.25 + rand() * 0.5;
    p.fwy = 0.2 + rand() * 0.45;
    p.ph = rand() * Math.PI * 2;
    p.spin = 0.4 + rand() * 0.9;
}
const particles = [];
for (let e = 0; e < EMITTERS.length; e++) {
    for (let i = 0; i < EMITTERS[e].n; i++) {
        const p = { e };
        respawn(p, true);
        particles.push(p);
    }
}
// the live blue field, rebuilt from the drifting particles every frame;
// created lazily so importing this module never touches the DOM (Node tests)
let field;
let fctx;
function ensureField() {
    if (fctx)
        return;
    sprite = buildSprite();
    field = makeSized(W, H);
    fctx = field.getContext('2d');
}
// One shared simulation clock: every veil canvas (loader, pairing, finishing)
// paints the same field, so the wave carries its state across screen
// transitions instead of restarting — and two visible canvases in the same
// frame advance the particles only once.
let fieldClock = -1;
function renderField(now) {
    ensureField();
    const dt = fieldClock < 0 ? 0 : Math.min(Math.max(now - fieldClock, 0) / 1000, 0.05);
    fieldClock = now;
    const { blue } = pal();
    fctx.globalCompositeOperation = 'source-over';
    fctx.clearRect(0, 0, W, H);
    fctx.globalCompositeOperation = 'lighter';
    for (const p of particles) {
        p.age += dt;
        if (p.age >= p.life)
            respawn(p, false);
        const lt = p.age / p.life;
        const x = p.x0 + p.vx * p.age + Math.sin(p.age * p.fwx + p.ph) * p.swx;
        const y = p.y0 + p.vy * p.age + Math.cos(p.age * p.fwy + p.ph * 1.7) * p.swy;
        const r = p.r0 * (1 + lt * p.rg);
        const fadeIn = Math.min(lt / 0.2, 1);
        const fadeOut = Math.min((1 - lt) / 0.4, 1);
        const flicker = 0.82 + 0.18 * Math.sin(p.age * p.spin + p.ph);
        fctx.globalAlpha = p.base * Math.max(0, fadeIn) * Math.max(0, fadeOut) * flicker;
        fctx.drawImage(sprite, x - r, y - r, r * 2, r * 2);
    }
    fctx.globalAlpha = 1;
    // colourise the accumulated alpha with the wave blue
    fctx.globalCompositeOperation = 'source-in';
    fctx.fillStyle = `rgb(${blue[0]},${blue[1]},${blue[2]})`;
    fctx.fillRect(0, 0, W, H);
    fctx.globalCompositeOperation = 'source-over';
}
const caches = new WeakMap();
// Build the size-dependent, time-independent layers once per resize (and per
// scheme flip — the grid colour is baked in).
function ensureCache(canvas) {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height)
        return null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
    }
    const prev = caches.get(canvas);
    if (prev && prev.w === w && prev.h === h && prev.dark === dark)
        return prev;
    const cell = Math.max(2, Math.round(CELL * dpr));
    // cover-fit the 1440×839 art, anchored top-centre
    const s = Math.max(w / W, h / H);
    const dw = W * s;
    const dh = H * s;
    const ox = (w - dw) / 2;
    const cols = Math.ceil(w / cell);
    const rows = Math.ceil(h / cell);
    // static grid layer, fading out toward the right and the bottom
    const grid = makeSized(w, h);
    const gc = grid.getContext('2d');
    gc.strokeStyle = pal().gridLine;
    gc.lineWidth = 1;
    gc.beginPath();
    for (let x = 0; x <= w; x += cell) {
        gc.moveTo(x + 0.5, 0);
        gc.lineTo(x + 0.5, h);
    }
    for (let y = 0; y <= h; y += cell) {
        gc.moveTo(0, y + 0.5);
        gc.lineTo(w, y + 0.5);
    }
    gc.stroke();
    gc.globalCompositeOperation = 'destination-in';
    const gh = gc.createLinearGradient(0, 0, w, 0);
    gh.addColorStop(0, 'rgba(0,0,0,1)');
    gh.addColorStop(0.35, 'rgba(0,0,0,0.92)');
    gh.addColorStop(0.7, 'rgba(0,0,0,0)');
    gh.addColorStop(1, 'rgba(0,0,0,0)');
    gc.fillStyle = gh;
    gc.fillRect(0, 0, w, h);
    const gv = gc.createLinearGradient(0, 0, 0, h);
    gv.addColorStop(0, 'rgba(0,0,0,1)');
    gv.addColorStop(GRID_FADE, 'rgba(0,0,0,1)');
    gv.addColorStop(1, 'rgba(0,0,0,0)');
    gc.fillStyle = gv;
    gc.fillRect(0, 0, w, h);
    gc.globalCompositeOperation = 'source-over';
    const c = {
        w,
        h,
        dpr,
        dark,
        cell,
        s,
        dw,
        dh,
        ox,
        cols,
        rows,
        scene: makeSized(w, h),
        small: makeSized(cols, rows),
        pixc: makeSized(w, h),
        grid,
    };
    caches.set(canvas, c);
    return c;
}
// Render one frame at timestamp `now` (ms, performance.now()-compatible).
function paint(canvas, now) {
    const C = ensureCache(canvas);
    if (!C)
        return false;
    const { w, h, dpr, cell, s, dw, dh, ox, cols, rows, scene, small, pixc, grid } = C;
    const { base, baseRgb, fieldAlpha, clearAlpha } = pal();
    const ctx = canvas.getContext('2d');
    renderField(now);
    // 1) smooth scene = base + blue field (cover-fit, top-anchored)
    const sc = scene.getContext('2d');
    sc.globalCompositeOperation = 'source-over';
    sc.fillStyle = base;
    sc.fillRect(0, 0, w, h);
    sc.imageSmoothingEnabled = true;
    sc.globalAlpha = fieldAlpha;
    sc.drawImage(field, 0, 0, W, H, ox, 0, dw, dh);
    sc.globalAlpha = 1;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(scene, 0, 0);
    // 2) mosaic: quantise the scene to the grid, masked to the left half
    const smc = small.getContext('2d');
    smc.imageSmoothingEnabled = true;
    smc.clearRect(0, 0, cols, rows);
    smc.drawImage(scene, 0, 0, cols, rows);
    const pc = pixc.getContext('2d');
    pc.globalCompositeOperation = 'source-over';
    pc.clearRect(0, 0, w, h);
    pc.imageSmoothingEnabled = false;
    pc.drawImage(small, 0, 0, cols, rows, 0, 0, cols * cell, rows * cell);
    pc.globalCompositeOperation = 'destination-in';
    const edge = ox + PIX_EDGE * s;
    const soft = PIX_SOFT * s;
    const pm = pc.createLinearGradient(edge - soft, 0, edge + soft, 0);
    pm.addColorStop(0, 'rgba(0,0,0,1)');
    pm.addColorStop(1, 'rgba(0,0,0,0)');
    pc.fillStyle = pm;
    pc.fillRect(0, 0, w, h);
    ctx.drawImage(pixc, 0, 0);
    // 3) fade the wave down to the base colour (band across the top)
    const fade = ctx.createLinearGradient(0, 0, 0, h);
    fade.addColorStop(0, `rgba(${baseRgb},0)`);
    fade.addColorStop(FADE_START, `rgba(${baseRgb},0)`);
    fade.addColorStop(FADE_END, `rgba(${baseRgb},1)`);
    fade.addColorStop(1, `rgba(${baseRgb},1)`);
    ctx.fillStyle = fade;
    ctx.fillRect(0, 0, w, h);
    // 3b) keep-clear pool behind the pairing content so text stays readable
    // (per-canvas data-veil-clear overrides the app-wide anchor — the loader and
    // finishing screens center their content even in the kiosk layout)
    const anchorAttr = canvas.dataset.veilClear;
    const anchor = anchorAttr === 'center' || anchorAttr === 'left' ? anchorAttr : clearAnchor;
    const cx = anchor === 'center' ? w / 2 : CLEAR_LEFT_X * dpr;
    const cy = anchor === 'center' ? h * 0.45 : CLEAR_LEFT_Y * dpr;
    const r0 = (anchor === 'center' ? CLEAR_CENTER_R0 : CLEAR_R0) * dpr;
    const r1 = (anchor === 'center' ? CLEAR_CENTER_R1 : CLEAR_R1) * dpr;
    const kg = ctx.createRadialGradient(cx, cy, r0, cx, cy, r1);
    kg.addColorStop(0, `rgba(${baseRgb},${clearAlpha})`);
    kg.addColorStop(1, `rgba(${baseRgb},0)`);
    ctx.fillStyle = kg;
    ctx.fillRect(0, 0, w, h);
    // 4) static grid on top
    ctx.drawImage(grid, 0, 0);
    return true;
}
function initCanvas(canvas) {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    // Fade the canvas in over the flat background once the first real frame has
    // painted (the pairing screen may still be display:none at script time).
    let revealed = false;
    const reveal = () => {
        if (revealed)
            return;
        revealed = true;
        if (!reduce)
            canvas.style.transition = 'opacity 0.7s ease-out';
        canvas.style.opacity = '1';
    };
    if (paint(canvas, performance.now()))
        reveal();
    if (reduce) {
        const ro = new ResizeObserver(() => {
            if (paint(canvas, performance.now()))
                reveal();
        });
        ro.observe(canvas);
        liveCanvases.set(canvas, () => ro.disconnect());
        return;
    }
    let raf = 0;
    let lastTs = 0;
    let running = false;
    const loop = (now) => {
        if (now - lastTs >= 33) {
            // ~30fps; the shared field clock inside paint advances the smoke
            if (paint(canvas, now))
                reveal();
            lastTs = now;
        }
        raf = requestAnimationFrame(loop);
    };
    const play = () => {
        if (!running) {
            running = true;
            lastTs = 0;
            raf = requestAnimationFrame(loop);
        }
    };
    const stop = () => {
        running = false;
        cancelAnimationFrame(raf);
    };
    // only animate while the pairing screen is actually shown
    const io = new IntersectionObserver(entries => (entries[0]?.isIntersecting ? play() : stop()), {
        threshold: 0,
    });
    io.observe(canvas);
    const ro = new ResizeObserver(() => {
        caches.delete(canvas);
        if (!running && paint(canvas, performance.now()))
            reveal();
    });
    ro.observe(canvas);
    liveCanvases.set(canvas, () => {
        stop();
        io.disconnect();
        ro.disconnect();
    });
}
// Scheme + clear anchor are app-wide and resolved once: the first caller's
// options win, later callers only add canvases.
function ensureScheme(options) {
    if (schemeReady)
        return;
    schemeReady = true;
    const scheme = options?.scheme ?? 'auto';
    clearAnchor = options?.clear ?? 'left';
    if (scheme === 'auto') {
        const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
        dark = mq?.matches ?? false;
        // Live scheme switch: flip the CSS class and repaint (ensureCache rebuilds
        // the grid layer when the baked scheme no longer matches).
        mq?.addEventListener?.('change', e => {
            dark = e.matches;
            applyDarkClasses();
            for (const canvas of liveCanvases.keys())
                paint(canvas, performance.now());
        });
    }
    else {
        dark = scheme === 'dark';
    }
    applyDarkClasses();
}
/** Drives every `canvas[data-pair-veil]` in the document that isn't live yet. */
export function initPairVeil(options) {
    ensureScheme(options);
    document.querySelectorAll('canvas[data-pair-veil]').forEach(c => mountPairVeil(c, options));
}
/** Drives one canvas that isn't in the document when initPairVeil runs (the web instance picker). */
export function mountPairVeil(canvas, options) {
    ensureScheme(options);
    if (!liveCanvases.has(canvas))
        initCanvas(canvas);
}
/** Stops the loop and releases the per-canvas layers (several viewport-sized bitmaps). */
export function unmountPairVeil(canvas) {
    liveCanvases.get(canvas)?.();
    liveCanvases.delete(canvas);
    caches.delete(canvas);
}
//# sourceMappingURL=pairVeil.js.map